import { apiRequest } from "@/lib/api";
import {
  putFileToPresignedUrl,
  validateNavImageFile,
} from "@/lib/api/navImageUpload";
import type { Article, ArticleWritePayload } from "@/types/article";

export type ArticleListResponse = {
  statusCode: number;
  message: string;
  data: {
    articles: Article[];
    pagination: { page: number; limit: number; total: number };
  };
};

export type ArticleOneResponse = {
  statusCode: number;
  message: string;
  data: { article: Article };
};

type CoverPresignResponse = {
  statusCode: number;
  message: string;
  data: {
    uploadUrl: string;
    imageUrl: string;
    expiresIn: number;
  };
};

function listRequest(filters?: Record<string, string | number | boolean | undefined>) {
  const params = new URLSearchParams();
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") return;
      params.append(key, String(value));
    });
  }
  const qs = params.toString();
  return apiRequest<ArticleListResponse>(`/api/admin/articles${qs ? `?${qs}` : ""}`);
}

export const articlesApi = {
  list: (filters?: Record<string, string | number | boolean | undefined>) =>
    listRequest(filters),

  get: (articleId: string) =>
    apiRequest<ArticleOneResponse>(`/api/admin/articles/${encodeURIComponent(articleId)}`),

  create: (payload: ArticleWritePayload) =>
    apiRequest<ArticleOneResponse>("/api/admin/articles", {
      method: "POST",
      body: payload,
    }),

  update: (articleId: string, payload: Partial<ArticleWritePayload>) =>
    apiRequest<ArticleOneResponse>(`/api/admin/articles/${encodeURIComponent(articleId)}`, {
      method: "PATCH",
      body: payload,
    }),

  unpublish: (articleId: string) =>
    apiRequest<ArticleOneResponse>(
      `/api/admin/articles/${encodeURIComponent(articleId)}/unpublish`,
      { method: "PATCH" }
    ),

  remove: (articleId: string) =>
    apiRequest<ArticleOneResponse>(`/api/admin/articles/${encodeURIComponent(articleId)}`, {
      method: "DELETE",
    }),

  presignCoverUpload: (body: { fileName: string; fileType: string; fileSize: number }) =>
    apiRequest<CoverPresignResponse>("/api/admin/articles/upload-cover-image", {
      method: "POST",
      body,
    }),
};

/**
 * Upload a cover image to public R2 via presigned URL; returns the CDN URL to save on the article.
 */
export async function uploadArticleCoverImage(file: File): Promise<string> {
  validateNavImageFile(file);
  const presign = await articlesApi.presignCoverUpload({
    fileName: file.name,
    fileType: file.type,
    fileSize: file.size,
  });
  await putFileToPresignedUrl(presign.data.uploadUrl, file);
  return presign.data.imageUrl;
}
